import React from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  Beaker,
  Building2,
  CheckCircle2,
  Cog,
  Users,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();

  const capabilities = [
    { icon: Beaker, key: 'labs' },
    { icon: Award, key: 'calibration' },
    { icon: Cog, key: 'manufacturing' },
    { icon: CheckCircle2, key: 'qa' },
    { icon: Building2, key: 'engineering' },
    { icon: Users, key: 'support' },
  ];

  const values = [
    { key: 'innovation', bg: 'bg-[#0A1F44]' },
    { key: 'quality', bg: 'bg-[#FF6A00]' },
    { key: 'reliability', bg: 'bg-[#3A3A3A]' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-[#0A1F44] text-white py-12 sm:py-16 md:py-20">
        <div className="max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto px-4 sm:px-6 text-center md:text-left">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
            {t('about.header.title')}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl leading-relaxed mx-auto md:mx-0">
            {t('about.header.description')}
          </p>
        </div>
      </section>

      <div className="max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16 space-y-16">
        {/* Company Overview */}
        <div className="bg-white rounded-lg p-6 sm:p-8 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Text */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1F44] mb-4 sm:mb-6">
                {t('about.whoWeAre.title')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                {t('about.whoWeAre.p1')}
              </p>
              <p className="text-gray-700 leading-relaxed mb-6 text-sm sm:text-base">
                {t('about.whoWeAre.p2')}
              </p>

              <div className="flex flex-col sm:flex-row items-center sm:items-start justify-center sm:justify-start gap-6 sm:gap-8 text-center sm:text-left">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#FF6A00] mb-1">
                    175 °C / 100 MPa
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('about.whoWeAre.stats.hthp')}
                  </div>
                </div>
                <div className="sm:border-l sm:border-r border-gray-300 sm:px-6">
                  <div className="text-2xl sm:text-3xl font-bold text-[#FF6A00] mb-1">
                    8 Sections
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('about.whoWeAre.stats.modular')}
                  </div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#FF6A00] mb-1">
                    ISO 9001 / API Q1
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('about.whoWeAre.stats.certified')}
                  </div>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="relative h-64 sm:h-80 md:h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="/RL54.png"
                alt="Precision manufacturing facility"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Expertise Areas */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1F44] mb-6 sm:mb-8 text-center">
            {t('about.expertise.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {t('about.expertise.items', { returnObjects: true }).map((item, index) => (
              <Card
                key={index}
                className="border-none shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <CardContent className="p-5 sm:p-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#FF6A00]/10 rounded-lg flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[#FF6A00]/20 transition-colors duration-300">
                    <Cog className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF6A00]" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-[#0A1F44] mb-1">
                    {item}
                  </h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Capabilities */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1F44] mb-6 sm:mb-8 text-center">
            {t('about.capabilities.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <Card
                  key={index}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF6A00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-7 w-7 sm:h-8 sm:w-8 text-[#FF6A00]" />
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-[#0A1F44] mb-1 sm:mb-2">
                      {t(`about.capabilities.items.${capability.key}.title`)}
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                      {t(`about.capabilities.items.${capability.key}.desc`)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Certifications */}
        <section className="bg-white rounded-lg p-6 sm:p-8 shadow-lg">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1F44] mb-6 sm:mb-8 text-center">
            {t('about.certifications.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {t('about.certifications.items', { returnObjects: true }).map((cert, index) => (
              <div
                key={index}
                className="text-center p-5 sm:p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF6A00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-6 w-6 sm:h-8 sm:w-8 text-[#FF6A00]" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-[#0A1F44] leading-snug">
                  {cert}
                </h3>
              </div>
            ))}
          </div>
        </section>

        {/* Values */}
        <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
          {values.map((value, index) => (
            <Card key={index} className={`border-none shadow-lg ${value.bg} text-white`}>
              <CardContent className="p-6 sm:p-8 text-center">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
                  {t(`about.values.${value.key}.title`)}
                </h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  {t(`about.values.${value.key}.desc`)}
                </p>
              </CardContent>
            </Card>
          ))}
        </section>
      </div>

      {/* CTA */}
      <section className="bg-[#0A1F44] text-white py-12 sm:py-16 md:py-20 text-center">
        <div className="max-w-[95%] sm:max-w-[90%] md:max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
            {t('about.cta.title')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed">
            {t('about.cta.description')}
          </p>
          <a href="mailto:support@hh-enterprise.net">
            <Button
              size="lg"
              className="bg-[#FF6A00] hover:bg-[#E55F00] text-white px-6 py-3 text-sm sm:text-base transition-all duration-300"
            >
              {t('about.cta.button')}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
